import { Link } from "react-router-dom"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Crown, Loader2, Infinity as InfinityIcon } from "lucide-react"
import { useAuth } from "@/contexts/auth-context"
import { useCurrentLicense } from "@/hooks/use-licenses"
import { useSidebar } from "@/components/ui/sidebar"
import { cn } from "@/lib/utils"

interface UserCardProps {
  className?: string
}

export function UserCard({ className }: UserCardProps) {
  const { user } = useAuth()
  const { data: license, isLoading: licenseLoading } = useCurrentLicense()
  const { setOpenMobile, isMobile } = useSidebar()

  // Function to handle navigation and close sidebar on mobile
  const handleNavigation = () => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  // Generate avatar fallback from user's initials if available
  const getUserInitials = () => {
    if (!user) return "JD"
    const firstInitial = user.first_name ? user.first_name.charAt(0) : ""
    const lastInitial = user.last_name ? user.last_name.charAt(0) : ""
    return (firstInitial + lastInitial).toUpperCase() || "JD"
  }

  // Get full name for display
  const getUserName = () => {
    if (!user) return "Guest"
    return `${user.first_name} ${user.last_name}`.trim() || user.email
  }

  const getUserEmail = () => {
    if (!user) return "Guest"
    return user.email
  }

  const getLicenseBadgeVariant = (type: string) => {
    return type === "pro" ? "default" : "secondary"
  }

  const getLicenseDisplayName = (type: string) => {
    return type === "pro" ? "Pro" : "Free"
  }

  // Calculate progress percentages for games
  const getGameProgress = () => {
    if (!license) return null

    if (license.license_type === "pro") {
      return { type: "pro", unlimited: true }
    }

    // For free users, calculate progress for both game types
    const proProgress = license.pro_games.limit > 0
      ? Math.max(0, (license.pro_games.used_this_month / license.pro_games.limit) * 100)
      : 0

    const freeProgress = license.free_games.limit > 0 && !license.free_games.unlimited
      ? Math.max(0, (license.free_games.used_this_month / license.free_games.limit) * 100)
      : 0

    return {
      type: "free",
      pro: {
        percentage: Math.min(100, proProgress),
        remaining: license.pro_games.remaining,
        used: license.pro_games.used_this_month,
        limit: license.pro_games.limit
      },
      free: {
        percentage: Math.min(100, freeProgress),
        remaining: license.free_games.remaining,
        unlimited: license.free_games.unlimited,
        used: license.free_games.used_this_month,
        limit: license.free_games.limit
      }
    }
  }

  const gameProgress = getGameProgress()

  if (!user) return null

  return (
    <Link to="/profile" onClick={handleNavigation} className="block">
      <div
        className={cn(
          "relative flex flex-col rounded-lg bg-muted/50 hover:bg-muted transition-colors text-card-foreground cursor-pointer group/usercard border",
          className
        )}
      >
        {/* License Badge - positioned in top right */}
        <div className="absolute -top-3 -right-2 z-10">
          {licenseLoading ? (
            <div className="flex items-center justify-center w-6 h-6 bg-background rounded-full border">
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
            </div>
          ) : license ? (
            <Badge
              variant={getLicenseBadgeVariant(license.license_type)}
              className={cn(
                "text-xs px-2 py-0.5 shadow-sm",
                license.license_type === "pro" && "bg-gradient-to-r from-yellow-500 to-yellow-600 text-white border-yellow-400"
              )}
            >
              {license.license_type === "pro" && <Crown className="h-3 w-3 mr-1" />}
              {getLicenseDisplayName(license.license_type)}
            </Badge>
          ) : null}
        </div>

        {/* Main card content */}
        <div className="flex items-center gap-3 p-3">
          <Avatar className="h-10 w-10 group-hover/usercard:scale-105 transition-transform">
            <AvatarFallback className="group-hover/usercard:bg-primary/30 transition-colors">{getUserInitials()}</AvatarFallback>
          </Avatar>

          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-sm font-medium text-foreground truncate group-hover/usercard:text-primary transition-colors">
              {getUserName()}
            </span>
            <span className="text-xs text-muted-foreground truncate">
              {getUserEmail()}
            </span>
          </div>
        </div>

        {/* Progress indicators */}
        {gameProgress && !licenseLoading && (
          <div className="px-3 pb-2">
            {gameProgress.type === "pro" ? (
              // Pro user: Single dark blue bar with infinity
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-blue-600 dark:text-blue-400 flex items-center gap-1">
                    <InfinityIcon className="h-3 w-3" />
                    <span>Unlimited</span>
                  </span>
                </div>
                <div className="h-1.5 bg-secondary group-hover/usercard:bg-blue-200 dark:group-hover/usercard:bg-blue-800 rounded-full overflow-hidden transition-colors">
                  <div className="h-full bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-500 dark:to-blue-600 w-full rounded-full animate-pulse" />
                </div>
              </div>
            ) : gameProgress.type === "free" && gameProgress.pro && gameProgress.free ? (
              // Free user: Two thin progress bars
              <div className="space-y-1.5">
                {/* Pro games progress */}
                <div className="space-y-0.5">
                  <div>
                    <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                      Pro: {gameProgress.pro.used}/{gameProgress.pro.limit}
                    </span>
                  </div>
                  <div className="h-1 bg-secondary group-hover/usercard:bg-primary/40 rounded-full overflow-hidden transition-colors">
                    <div
                      className={cn(
                        "h-full rounded-full transition-all duration-500",
                        gameProgress.pro.percentage >= 80
                          ? "bg-gradient-to-r from-red-500 to-red-600"
                          : gameProgress.pro.percentage >= 60
                          ? "bg-gradient-to-r from-yellow-500 to-yellow-600"
                          : "bg-gradient-to-r from-orange-500 to-orange-600"
                      )}
                      style={{ width: `${gameProgress.pro.percentage}%` }}
                    />
                  </div>
                </div>

                {/* Free games progress (only show if limited) */}
                {!gameProgress.free.unlimited && gameProgress.free.limit > 0 && (
                  <div className="space-y-0.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                        Free: {gameProgress.free.remaining}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {gameProgress.free.used}/{gameProgress.free.limit}
                      </span>
                    </div>
                    <div className="h-1 bg-secondary group-hover/usercard:bg-blue-200 dark:group-hover/usercard:bg-blue-800 rounded-full overflow-hidden transition-colors">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
                        style={{ width: `${gameProgress.free.percentage}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Free unlimited indicator */}
                {gameProgress.free.unlimited && (
                  <div className="flex items-center justify-center">
                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400 flex items-center gap-1">
                      <span>Free: </span>
                      <InfinityIcon className="h-2.5 w-2.5" />
                    </span>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </Link>
  )
}
